import { type BuiltinToolName, ToolNames } from '@kanbu/schema/contracts';
import { Agent, AgentType } from '@kanbu/schema/enums';

/**
 * Which built-ins the superadmin `enabledBuiltinTools` multiselect offers.
 *
 * Kept beside the field rather than derived from `builtinToolCatalog`, because
 * the catalog describes what a tool *is* and this describes how it is *gated* —
 * and gating is not a property with a small set of values. `isBuiltinToolEnabled`
 * in `resolveTools.ts` has six branches, three of which are conjunctions or
 * disjunctions rather than states, so an enum on the catalog would have to lie
 * about at least three tools.
 *
 * What it is instead is an exhaustive partition of `BuiltinToolNames`, enforced
 * by `__tests__/builtinToolOptions.test.ts`. That test is the whole point: the
 * map silently lagged behind the tool list for a whole release, which is why
 * `fetchUrl`, `findDocuments`, `getDocumentOverview` and `getSimilarProducts`
 * were all unreachable from the admin UI despite being fully implemented. The
 * omission presents as "the feature does not work", with nothing pointing here.
 */

/** Offered on every agent that supports built-in tool selection. */
export const SHARED_BUILTIN_TOOL_OPTIONS = [
  ToolNames.DocumentSearch,
  ToolNames.FindDocuments,
  ToolNames.GetDocumentOverview,
  ToolNames.FetchUrl,
  ToolNames.GetOperator,
  ToolNames.UpdateWorkingMemory,
] as const satisfies readonly BuiltinToolName[];

/**
 * Offered only for the shop assistant. `resolveTools` vetoes every one of
 * these on any other persona, so listing them elsewhere would offer a switch
 * that cannot turn anything on.
 */
export const SHOP_BUILTIN_TOOL_OPTIONS = [
  ToolNames.GetShopProducts,
  ToolNames.GetSimilarProducts,
  ToolNames.GetShopProductDetails,
  ToolNames.GetShopCategories,
  ToolNames.GetShopDeliveryOptions,
] as const satisfies readonly BuiltinToolName[];

export const CHAT_BUILTIN_TOOL_OPTIONS = [
  ToolNames.SeeImage,
  ToolNames.ViewPage,
  ToolNames.SearchPdf,
] as const satisfies readonly BuiltinToolName[];

export const VOICE_BUILTIN_TOOL_OPTIONS = [
  ToolNames.RecordAnswerSources,
] as const satisfies readonly BuiltinToolName[];

/**
 * Built-ins that must **not** appear in the multiselect, each because its
 * allowlist entry is not what decides whether it runs. A checkbox here would
 * be inert, which is worse than absent.
 *
 * - `webSearch` — governed by the dedicated `hasWebSearch` config toggle.
 *   Two switches for one capability is a support ticket waiting to happen.
 * - `recommendShopProducts` / `showShopProductDetail` — product cards render
 *   exclusively through these, so `isBuiltinToolEnabled` keys them off
 *   `getShopProducts`'s entry and ignores their own entirely.
 * - `transferCall` / `endCall` — voice-only and appended by the voice
 *   runtime, not this multiselect.
 */
export const UNOFFERED_BUILTIN_TOOLS = [
  ToolNames.WebSearch,
  ToolNames.RecommendShopProducts,
  ToolNames.ShowShopProductDetail,
  ToolNames.TransferCall,
  ToolNames.EndCall,
] as const satisfies readonly BuiltinToolName[];

export type OfferedBuiltinToolName =
  | (typeof SHARED_BUILTIN_TOOL_OPTIONS)[number]
  | (typeof SHOP_BUILTIN_TOOL_OPTIONS)[number]
  | (typeof CHAT_BUILTIN_TOOL_OPTIONS)[number]
  | (typeof VOICE_BUILTIN_TOOL_OPTIONS)[number];

/** Names the multiselect should show for this agent persona. */
export function offeredBuiltinToolNames(
  agent: Agent | undefined,
  agentType?: AgentType,
): BuiltinToolName[] {
  return [
    ...SHARED_BUILTIN_TOOL_OPTIONS,
    ...(agentType === AgentType.Voice ? [] : CHAT_BUILTIN_TOOL_OPTIONS),
    ...(agentType === AgentType.Voice ? VOICE_BUILTIN_TOOL_OPTIONS : []),
    ...(agent === Agent.ShopAssistant ? SHOP_BUILTIN_TOOL_OPTIONS : []),
  ];
}
